import { z } from "zod";
import { Timestamp } from "./common.ts";

/**
 * Observed precipitation RADAR on the overview map, inside the "Niederschlag" overlay. Owner
 * (2026-11-03): one time bar, the past 2 h are the measured radar, from now on the forecast
 * (precip.ts). Plan: ~/.claude-awl/plans/rain-radar.md. This block is binding.
 *
 * Source: MeteoSwiss open data, STAC collection `ch.meteoschweiz.ogd-radar-precip`
 * (RADAR_STAC_COLLECTION), product RZC ("PRECIP"): instantaneous rain rate in mm/h, 1 km grid in
 * LV95, one file every 5 minutes, ~4 min latency, ODIM_H5. Licence CC BY 4.0: the UI credits
 * RADAR_ATTRIBUTION with a link to RADAR_ATTRIBUTION_URL and RADAR_LICENCE_URL, and says the
 * data were reprojected. MeteoSwiss does not endorse us; never imply it.
 *
 * API (binding):
 * - Only the API talks to MeteoSwiss. Lazy refresh: a request to the index starts (without
 *   waiting) at most one refresh per RADAR_REFRESH_MS, coalesced across requests; no timer runs
 *   while nobody looks.
 * - Listing: GET today's STAC item (`YYYYMMDD-ch`, UTC) with `If-None-Match` (it carries an
 *   ETag; ~456 assets: cpc, rzc, tzc). Also yesterday's item when today's is missing, or
 *   within RADAR_WINDOW_MS + RADAR_STALE_MS after 00:00 UTC. A missing item (404, typical
 *   just after 00:00) is not an error and never stops the other day's listing.
 * - Candidates: asset keys parsed with `parseRzcKey` (`rzc` + YY DDD HHMM, day of year from 1,
 *   END time of the interval, UTC). Keys are only parsed to pick candidates, never built.
 *   Download newest first, one at a time, only candidates inside the window that are not held
 *   yet. The ODIM end time (`what/enddate` + `endtime`) is authoritative: a file whose end time
 *   differs from its key, or that is off the 5-minute grid, is discarded.
 * - Frames are keyed by frame time. The first frame held for a time wins and is never replaced
 *   (matches immutable caching; upstream reprocessing is ignored).
 * - Anchor: the window is [newest − RADAR_WINDOW_MS, newest], newest = the newest end time
 *   listed in STAC (so the API fills all 25 frames), ignoring keys later than now +
 *   RADAR_STEP_MS (a mislabelled asset must not empty the radar). Held frames outside it are
 *   dropped.
 * - File failures: an HTTP or network error on one file skips that file for this refresh only
 *   (retried next time) and the loop goes on; the refresh stops after 3 consecutive file
 *   failures or when the listing fails. A file that fails validation is not retried while its
 *   time is in the window; environment errors (temp dir, disk) count as failures, never as
 *   validation. Hrefs are resolved against the item URL before the same-origin check. Bodies
 *   are read with a running size cap.
 * - `fetchedAt` is the time of the last successful LISTING, never the serve time (the index
 *   and its ETag change only when the content changes).
 * - Staleness: when the newest held frame is older than now − RADAR_STALE_MS, the radar counts
 *   as unavailable: the index answers 503 (`Retry-After: 60`, `no-store`) until a fresh frame
 *   arrives. "Jetzt" never labels an old frame.
 * - Upstream failures never throw into the request; after a failed refresh the next waits
 *   RADAR_REFRESH_MS like any other.
 * - Decode with h5wasm (or equivalent, no native build). Close AND unlink every file written to
 *   its virtual FS after decoding (it keeps them otherwise).
 *   - Validate against RADAR_RZC_GRID: shape, `where` (xscale/yscale, corner geometry), projdef
 *     (somerc, LV95 false origin), `gain` 1, `offset` 0, unit mm/h. Any mismatch → discard the
 *     file (logged), never guess. Never derive the origin from the float corner lat/lon.
 *   - Warp onto RADAR_FRAME: cell centre `radarCellCenter(i, j)` → `wgs84ToLv95` → RZC cell
 *     `col = floor((E − e0) / cellM)`, `row = floor((n0 − N) / cellM)` (row 0 = north). Outside
 *     the RZC grid → RADAR_NODATA. NaN / ODIM nodata or negative → RADAR_NODATA; ODIM undetect
 *     (0) and values < 0.05 → 0 (dry); others → `radarUnits(mm/h)`. Never fabricate dry.
 * - Frames are held in memory only (disposable; after a restart the first request refetches,
 *   newest first, so a 1-frame index is "now", not 2 h ago). Each frame's gzip body is computed
 *   once and kept with it.
 * - Routes:
 *   - `GET /api/weather/radar` (routes.radar) → RadarIndex. `Cache-Control: public,
 *     max-age=60`, ETag (strong, weak, `*` → 304). 503 + `Retry-After` + `no-store` while no
 *     fresh frame exists: `Retry-After: 5` while no frame is held and a refresh is running
 *     (cold start; the request never waits), else 60. 404 with `WIND_ENABLED=0` (one switch
 *     for all weather overlays). Rate limit like wind, key `radar`.
 *   - `GET /api/weather/radar/v1/:stamp` (routes.radarFrame; `stamp` = `radarStamp(time)`, e.g.
 *     `202611031220`) → the frame encoded with `encodeRadarFrame`, content type
 *     RADAR_CONTENT_TYPE, gzip, `Cache-Control: public, max-age=31536000, immutable` (a stamp's
 *     content never changes; a new encoding gets a new `/v2`). Unknown, malformed or dropped
 *     stamp → 404 `no-store`. Rate limit key `radar-frame`, RADAR_FRAME_RATE_PER_MIN per IP.
 *   - The service worker never caches either (`/api/weather/*` stays "never", with explicit
 *     cache-rules tests for both paths); the browser's HTTP cache keeps the immutable frames.
 *
 * Client (binding):
 * - Index: fetched while "Niederschlag" is on; refetched every RADAR_REFRESH_MS while the
 *   overlay is on and the tab is visible; on 503 retried after `Retry-After`.
 * - Frames: fetch the newest first (it is the default step), the others in the background one at
 *   a time (or on scrub/play). A frame that fails (404, 429, network) shows "keine Radardaten" at
 *   its step; the layer stays alive. Never show a previous frame under another step's time.
 * - Time bar (one bar, index-based steps, a visible divider between "gemessen" and "Prognose"):
 *   - Radar steps = exactly the frames in the index (gaps are skipped; each step shows its own
 *     time).
 *   - Then the overlay's existing forecast steps (wind on: the wind bar's steps; precipitation
 *     only: `precipTimeRange` hours), starting at the first step strictly AFTER the newest frame.
 *     Precipitation there follows precip.ts (`precipHourAt`; "no data" without a sum).
 *   - Exactly one "Jetzt": the newest frame, labelled "Jetzt · gemessen HH:MM" (Europe/Zurich),
 *     and only while it is no older than RADAR_STALE_MS (rechecked every minute and on
 *     tab visibility, not only on fetch); forecast steps say "Prognose" with their time and
 *     never "Jetzt".
 *   - Radar unavailable (503, network, empty): the bar is exactly today's forecast bar (with its
 *     own "Jetzt"), plus "Radar derzeit nicht verfügbar". Forecast unavailable: radar steps only.
 *   - With wind on, radar steps show the MODEL wind of their own time (the wind field reaches
 *     WIND_PAST_HOURS before the current hour, wind.ts), so rain and wind animate together.
 *     "keine Winddaten" only at a step still before the wind field's first time (never a value
 *     from another time).
 *   - The wind layer is never unmounted or restarted by a time change (step, scrub, play): it stays
 *     mounted while wind is on and its field is loaded, particles keep flowing and only pick up
 *     the new time; at a step without wind it draws nothing (no particles, no arrows) until a step
 *     with wind comes back.
 * - Play/pause button loops the radar steps: never autoplays, pausable (WCAG 2.2.2),
 *   RADAR_PLAY_FRAME_MS per frame and RADAR_PLAY_HOLD_MS on the newest; with reduced motion no
 *   cross-fade.
 * - Draw a frame like the forecast: decode, map each cell to the `precip-*` class colours
 *   (`precipClass(radarMmH(v))`), nothing for dry or nodata, smooth scaling; Leaflet image
 *   bounds = RADAR_FRAME bounds (the grid is uniform in Web Mercator, so no skew). The legend
 *   notes that areas without radar data are not drawn.
 * - Readout at a radar step: "<place>: HH:MM gemessen 1.2 mm/h" / "kein Niederschlag" /
 *   "keine Radardaten" (nodata). RADAR_MAX_UNITS shows "≥ 25 mm/h".
 * - A one-line note in the info popover: radar can show artefacts, especially in the Alps.
 */
export const RADAR_STAC_COLLECTION = "ch.meteoschweiz.ogd-radar-precip";
export const RADAR_STAC_ITEMS_URL = `https://data.geo.admin.ch/api/stac/v1/collections/${RADAR_STAC_COLLECTION}/items`;
export const RADAR_ATTRIBUTION = "MeteoSwiss";
export const RADAR_ATTRIBUTION_URL =
  "https://opendatadocs.meteoswiss.ch/d-radar-data/d1-precipitation-radar-products";
export const RADAR_LICENCE_URL = "https://creativecommons.org/licenses/by/4.0/";

/** Product interval and how far back the bar goes (owner: 2 h). */
export const RADAR_STEP_MS = 5 * 60_000;
export const RADAR_WINDOW_MS = 2 * 60 * 60_000;
export const RADAR_REFRESH_MS = 5 * 60_000;
export const RADAR_FRAME_RATE_PER_MIN = 300;
/** Newest frame older than this → the radar counts as unavailable. */
export const RADAR_STALE_MS = 30 * 60_000;
export const RADAR_PLAY_FRAME_MS = 500;
export const RADAR_PLAY_HOLD_MS = 1500;

/**
 * The RZC input grid (verified 2026-11-03 against a live file): LV95 outer corners E 2 255 000 –
 * 2 965 000, N 840 000 – 1 480 000, 1 km cells, 710 columns × 640 rows, row 0 = north.
 */
export const RADAR_RZC_GRID = {
  e0: 2_255_000,
  n0: 1_480_000,
  cellM: 1000,
  nx: 710,
  ny: 640,
} as const;

/** RZC cell containing LV95 (E, N), or null outside the grid. */
export function rzcCellAt(e: number, n: number): { col: number; row: number } | null {
  const g = RADAR_RZC_GRID;
  const col = Math.floor((e - g.e0) / g.cellM);
  const row = Math.floor((g.n0 - n) / g.cellM);
  if (!(col >= 0 && col < g.nx && row >= 0 && row < g.ny)) return null;
  return { col, row };
}

/** One unit of a frame cell in mm/h; 0 = dry, RADAR_MAX_UNITS = "≥ 25 mm/h", RADAR_NODATA. */
export const RADAR_UNIT_MM_H = 0.1;
export const RADAR_MAX_UNITS = 250;
export const RADAR_NODATA = 255;

/**
 * The output grid, uniform in Web Mercator: cell (i, j) with i east, j SOUTH from the north-west
 * corner (row 0 = north), like images. ~0.6 km cells over Switzerland plus a margin.
 */
export const RADAR_FRAME = {
  west: 5.7,
  east: 10.8,
  south: 45.6,
  north: 48.0,
  width: 640,
  height: 440,
} as const;

export const RADAR_CONTENT_TYPE = "application/vnd.divespots.radar";

/** GET /api/weather/radar */
export const RadarIndex = z
  .object({
    source: z.literal("meteoswiss-rzc"),
    /** Frame times (end of each 5-minute interval, UTC), ascending, within RADAR_WINDOW_MS. */
    frames: z.array(Timestamp).min(1),
    /** When the API last refreshed successfully. */
    fetchedAt: Timestamp,
    attribution: z.string(),
  })
  .strict()
  .superRefine((idx, ctx) => {
    idx.frames.forEach((t, k) => {
      const ms = Date.parse(t);
      if (ms % RADAR_STEP_MS !== 0)
        ctx.addIssue({ code: "custom", path: ["frames", k], message: "5-minute steps only" });
      if (k > 0 && ms <= Date.parse(idx.frames[k - 1]!))
        ctx.addIssue({ code: "custom", path: ["frames", k], message: "frames must ascend" });
    });
    const first = Date.parse(idx.frames[0]!);
    const last = Date.parse(idx.frames[idx.frames.length - 1]!);
    if (last - first > RADAR_WINDOW_MS)
      ctx.addIssue({ code: "custom", path: ["frames"], message: "longer than the window" });
  });
export type RadarIndex = z.infer<typeof RadarIndex>;

/**
 * mm/h → frame unit; null, NaN or negative (not -0) → RADAR_NODATA (never dry); < 0.05 → 0;
 * capped at RADAR_MAX_UNITS.
 */
export function radarUnits(mmH: number | null): number {
  if (mmH === null || !Number.isFinite(mmH) || mmH < 0) return RADAR_NODATA;
  if (mmH < 0.05) return 0;
  return Math.min(RADAR_MAX_UNITS, Math.max(1, Math.round(mmH / RADAR_UNIT_MM_H)));
}

/** Frame unit → mm/h; RADAR_NODATA (or any value above RADAR_MAX_UNITS) → null. */
export function radarMmH(v: number): number | null {
  if (!(v >= 0 && v <= RADAR_MAX_UNITS)) return null;
  return v * RADAR_UNIT_MM_H;
}

/** `2026-11-03T12:20:00Z` → `202611031220` (UTC). Throws off the 5-minute grid. */
export function radarStamp(time: string | number): string {
  const ms = typeof time === "number" ? time : Date.parse(time);
  if (!Number.isFinite(ms) || ms % RADAR_STEP_MS !== 0) throw new Error("radar: bad frame time");
  return new Date(ms).toISOString().slice(0, 16).replace(/[-T:]/g, "");
}

/**
 * RZC asset key → frame END time (epoch ms), or null when it is not an RZC key, the date is
 * invalid or off the 5-minute grid. `rzc263070000rl.001.h5` → 2026-11-03T00:00Z.
 */
export function parseRzcKey(key: string): number | null {
  const m = /^rzc(\d{2})(\d{3})(\d{2})(\d{2})/.exec(key);
  if (!m) return null;
  const [y, doy, h, mi] = [2000 + Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])];
  if (doy < 1 || h > 23 || mi > 59) return null;
  const ms = Date.UTC(y, 0, 1, h, mi) + (doy - 1) * 86_400_000;
  if (new Date(ms).getUTCFullYear() !== y || ms % RADAR_STEP_MS !== 0) return null;
  return ms;
}

/** `202611031220` → epoch ms, or null when malformed or off the 5-minute grid. */
export function parseRadarStamp(stamp: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(stamp);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number) as [number, number, number, number, number, number];
  const ms = Date.UTC(y, mo - 1, d, h, mi);
  const back = new Date(ms);
  if (back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d || h > 23 || mi > 59) return null;
  if (ms % RADAR_STEP_MS !== 0) return null;
  // canonical only (Date.UTC maps years 0–99 to 1900+)
  return radarStamp(ms) === stamp ? ms : null;
}

const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const invMercY = (y: number) => (360 / Math.PI) * Math.atan(Math.exp(y)) - 90;

/** WGS84 centre of output cell (i, j); row 0 = north. Uniform in Web Mercator. */
export function radarCellCenter(i: number, j: number): { lat: number; lon: number } {
  const f = RADAR_FRAME;
  const lon = f.west + ((i + 0.5) / f.width) * (f.east - f.west);
  const yN = mercY(f.north);
  const yS = mercY(f.south);
  const lat = invMercY(yN - ((j + 0.5) / f.height) * (yN - yS));
  return { lat, lon };
}

/** Output cell containing (lat, lon), or null outside the frame. */
export function radarCellAt(lat: number, lon: number): { i: number; j: number } | null {
  const f = RADAR_FRAME;
  if (!(lon >= f.west && lon < f.east && lat > f.south && lat <= f.north)) return null;
  const i = Math.floor(((lon - f.west) / (f.east - f.west)) * f.width);
  const yN = mercY(f.north);
  const j = Math.floor(((yN - mercY(lat)) / (yN - mercY(f.south))) * f.height);
  return { i: Math.min(i, f.width - 1), j: Math.min(j, f.height - 1) };
}

/**
 * Binary frame v1, little-endian:
 *   0  4 bytes  magic "DSRD"
 *   4  u32      version (1)
 *   8  u16      width, u16 height (must equal RADAR_FRAME)
 *  12  f64      frame time, epoch ms
 *  20  u8[w*h]  cells, row-major, row 0 = north: 0 dry, 1..RADAR_MAX_UNITS, RADAR_NODATA
 * API encodes and client decodes ONLY with these functions.
 */
const MAGIC = 0x44525344; // "DSRD" read as LE u32
const HEADER = 20;

export interface RadarFrame {
  timeMs: number;
  cells: Uint8Array;
}

export function encodeRadarFrame({ timeMs, cells }: RadarFrame): ArrayBuffer {
  const { width, height } = RADAR_FRAME;
  if (cells.length !== width * height) throw new Error("radar: wrong cell count");
  radarStamp(timeMs);
  for (let k = 0; k < cells.length; k++) {
    const v = cells[k]!;
    if (v > RADAR_MAX_UNITS && v !== RADAR_NODATA) throw new Error(`radar: bad cell ${v}`);
  }
  const buf = new ArrayBuffer(HEADER + cells.length);
  const view = new DataView(buf);
  view.setUint32(0, MAGIC, true);
  view.setUint32(4, 1, true);
  view.setUint16(8, width, true);
  view.setUint16(10, height, true);
  view.setFloat64(12, timeMs, true);
  new Uint8Array(buf, HEADER).set(cells);
  return buf;
}

export function decodeRadarFrame(data: ArrayBuffer): RadarFrame {
  const { width, height } = RADAR_FRAME;
  if (data.byteLength !== HEADER + width * height) throw new Error("radar: bad length");
  const view = new DataView(data);
  if (view.getUint32(0, true) !== MAGIC) throw new Error("radar: bad magic");
  if (view.getUint32(4, true) !== 1) throw new Error("radar: unknown version");
  if (view.getUint16(8, true) !== width || view.getUint16(10, true) !== height)
    throw new Error("radar: bad size");
  const timeMs = view.getFloat64(12, true);
  radarStamp(timeMs);
  const cells = new Uint8Array(data.slice(HEADER));
  for (let k = 0; k < cells.length; k++) {
    const v = cells[k]!;
    if (v > RADAR_MAX_UNITS && v !== RADAR_NODATA) throw new Error(`radar: bad cell ${v}`);
  }
  return { timeMs, cells };
}
